// ─────────────────────────────────────────────────────────────────────────
// Creative Strategist Deliverables tab (Task #2382, spec §52).
//
// A project output library, not a list of rows. Three things separate the two:
//
//   * The card is the asset. A static ad shows the ad; a landing page shows
//     its hero; a document shows its opening lines. You recognise your work
//     before you read its title.
//   * Every card links OUT to the tool that owns the thing. Creative
//     Strategist does not try to be an ad editor — it hands you to My Ads and
//     the LP editor, where the real controls already live.
//   * The review is visible. Each card carries the verdict its latest attempt
//     earned, and opening it shows every attempt with the reason the system
//     went round again.
//
// Thumbnails go through the resizing proxy: a 40-card grid pulling 40
// full-size PNGs off storage is the difference between a library and a
// loading screen.
// ─────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  ArrowUpRight,
  Check,
  Copy,
  Download,
  ExternalLink,
  FileText,
  History,
  ImageIcon,
  Loader2,
  Package,
  Pencil,
  RefreshCw,
  Rocket,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  CS_DELIVERABLE_FILTERS,
  CS_DELIVERABLE_FILTER_LABELS,
  CS_REVIEW_VERDICT_CLASSES,
  CS_REVIEW_VERDICT_LABELS,
  type CsDeliverableFilter,
} from "@shared/creativeStrategistDeliverables";
import {
  csDeliverablesKey,
  csDeliverableVersionsKey,
  csFormatDateTime,
  csTasksKey,
  type CreativeStrategistDeliverableCard,
  type CreativeStrategistDeliverableVersion,
} from "@/lib/creative-strategist";

interface Props {
  projectId: string;
  brandId: string | undefined;
  /** Told to the chat panel so "make it bolder" lands on what is open. */
  onViewDeliverable?: (deliverableId: string | null) => void;
}

export function CreativeStrategistDeliverablesTab({
  projectId,
  brandId,
  onViewDeliverable,
}: Props) {
  const [filter, setFilter] = useState<CsDeliverableFilter>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<CreativeStrategistDeliverableCard[]>({
    queryKey: [csDeliverablesKey(projectId)],
    enabled: !!projectId,
    // Deliverables land while the user is looking at the tab.
    refetchInterval: 20_000,
  });

  const deliverables = data ?? [];
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of deliverables) map.set(d.category, (map.get(d.category) ?? 0) + 1);
    return map;
  }, [deliverables]);

  const visible =
    filter === "all" ? deliverables : deliverables.filter((d) => d.category === filter);

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  if (deliverables.length === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center space-y-2" data-testid="empty-deliverables">
          <Package className="h-6 w-6 mx-auto text-muted-foreground" />
          <div className="text-sm font-medium">No deliverables yet</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Finished work — ad sets, landing pages, documents — lands here for review and
            approval.
          </p>
        </CardContent>
      </Card>
    );
  }

  const open = deliverables.find((d) => d.id === openId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5" data-testid="deliverable-filters">
        {CS_DELIVERABLE_FILTERS.map((key) => {
          const count = key === "all" ? deliverables.length : counts.get(key) ?? 0;
          if (key !== "all" && count === 0) return null;
          return (
            <Button
              key={key}
              size="sm"
              variant={filter === key ? "default" : "outline"}
              className="h-7 text-xs"
              onClick={() => setFilter(key)}
              data-testid={`filter-${key}`}
            >
              {CS_DELIVERABLE_FILTER_LABELS[key]}
              <span className="ml-1.5 opacity-60">{count}</span>
            </Button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nothing under {CS_DELIVERABLE_FILTER_LABELS[filter]} yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="deliverable-grid">
          {visible.map((d) => (
            <DeliverableCard
              key={d.id}
              deliverable={d}
              brandId={brandId}
              onOpen={() => {
                setOpenId(d.id);
                onViewDeliverable?.(d.id);
              }}
            />
          ))}
        </div>
      )}

      <DeliverableDialog
        deliverable={open}
        projectId={projectId}
        brandId={brandId}
        onClose={() => {
          setOpenId(null);
          onViewDeliverable?.(null);
        }}
      />
    </div>
  );
}

// ── the card ──────────────────────────────────────────────────────────────

/**
 * Where this deliverable actually lives. Creative Strategist links out; it
 * does not re-implement the owning tool's editor.
 */
function toolLinks(
  d: CreativeStrategistDeliverableCard,
  brandId: string | undefined,
): Array<{ label: string; href: string; icon: any }> {
  const out: Array<{ label: string; href: string; icon: any }> = [];
  const system = d.output?.outputSystem;

  if (system === "lp_express" && d.output?.outputId) {
    const base = brandId ? `/brands/${brandId}/landing-pages-express` : "/landing-pages-express";
    out.push({ label: "View", href: `${base}?pageId=${d.output.outputId}`, icon: ExternalLink });
    out.push({
      label: "Edit",
      href: `${base}?pageId=${d.output.outputId}&mode=edit`,
      icon: Pencil,
    });
    out.push({
      label: "Publish",
      href: `${base}?pageId=${d.output.outputId}&publish=1`,
      icon: Rocket,
    });
    return out;
  }

  if (system === "ads_express") {
    const base = brandId ? `/brands/${brandId}/library` : "/library";
    out.push({ label: "Open in My Ads", href: base, icon: ExternalLink });
    return out;
  }

  if (d.output?.outputUrl) {
    out.push({ label: "Open", href: d.output.outputUrl, icon: ExternalLink });
  }
  return out;
}

function DeliverableCard({
  deliverable: d,
  brandId,
  onOpen,
}: {
  deliverable: CreativeStrategistDeliverableCard;
  brandId: string | undefined;
  onOpen: () => void;
}) {
  const links = toolLinks(d, brandId);
  const isDocument = !d.thumbnailUrl && !!d.documentContent;

  return (
    <Card className="overflow-hidden flex flex-col" data-testid={`deliverable-${d.id}`}>
      <button
        type="button"
        onClick={onOpen}
        className="block text-left"
        data-testid={`button-open-deliverable-${d.id}`}
      >
        {d.thumbnailUrl ? (
          <div className="aspect-[4/5] bg-muted overflow-hidden">
            <img
              src={d.thumbnailUrl}
              alt={d.title ?? d.deliverableType}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        ) : isDocument ? (
          <div className="aspect-[4/5] bg-muted/40 p-4 overflow-hidden">
            <p className="text-[11px] leading-relaxed text-muted-foreground line-clamp-[14] whitespace-pre-wrap">
              {d.documentContent}
            </p>
          </div>
        ) : (
          <div className="aspect-[4/5] bg-muted/40 flex items-center justify-center">
            {d.deliverableType.includes("page") ? (
              <FileText className="h-7 w-7 text-muted-foreground" />
            ) : (
              <ImageIcon className="h-7 w-7 text-muted-foreground" />
            )}
          </div>
        )}
      </button>

      <CardContent className="p-3.5 space-y-2.5 flex-1 flex flex-col">
        <div className="space-y-1 flex-1">
          <div className="text-sm font-medium leading-snug line-clamp-2">
            {d.title ?? d.deliverableType}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge
              variant="secondary"
              className={CS_REVIEW_VERDICT_CLASSES[d.reviewVerdict] ?? ""}
              data-testid={`verdict-${d.id}`}
            >
              {CS_REVIEW_VERDICT_LABELS[d.reviewVerdict] ?? d.reviewVerdict}
            </Badge>
            {d.versionCount > 1 && (
              <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                <History className="h-3 w-3" /> v{d.version} of {d.versionCount}
              </span>
            )}
          </div>
          {d.attentionReason && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300 flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{d.attentionReason}</span>
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {links.map((l) => (
            <Button
              key={l.label}
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              asChild
              data-testid={`link-${l.label.toLowerCase().replace(/\s+/g, "-")}-${d.id}`}
            >
              <a href={l.href} target="_blank" rel="noreferrer">
                <l.icon className="h-3 w-3 mr-1" />
                {l.label}
              </a>
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={onOpen}
            data-testid={`button-details-${d.id}`}
          >
            Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── the detail dialog: review, versions, approve, revise ──────────────────

function DeliverableDialog({
  deliverable: d,
  projectId,
  brandId,
  onClose,
}: {
  deliverable: CreativeStrategistDeliverableCard | null;
  projectId: string;
  brandId: string | undefined;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [request, setRequest] = useState("");

  const { data: history } = useQuery<{
    deliverableId: string;
    currentVersion: number;
    versions: CreativeStrategistDeliverableVersion[];
  }>({
    queryKey: [csDeliverableVersionsKey(d?.id)],
    enabled: !!d?.id,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [csDeliverablesKey(projectId)] });
    queryClient.invalidateQueries({ queryKey: [csTasksKey(projectId)] });
    if (d?.id) {
      queryClient.invalidateQueries({ queryKey: [csDeliverableVersionsKey(d.id)] });
    }
  };

  const approve = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/creative-strategist/deliverables/${d!.id}/approve`, {});
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Approved", description: "Marked as approved by you." });
    },
    onError: (e: any) =>
      toast({
        title: "Could not approve",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  const revise = useMutation({
    mutationFn: async (text: string) => {
      await apiRequest("POST", `/api/creative-strategist/deliverables/${d!.id}/revision`, {
        request: text,
      });
    },
    onSuccess: () => {
      setRequest("");
      invalidate();
      toast({
        title: "Revision started",
        description: "It will re-run and come back as a new version.",
      });
    },
    onError: (e: any) =>
      toast({
        title: "Could not start the revision",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  const versions = history?.versions ?? [];
  // The review shown here is the review OF WHAT IS ON SCREEN — the head. An
  // attempt that landed after the user approved stays in history without
  // becoming current, and must not label the approved asset with its verdict.
  const latest =
    versions.find((v) => v.versionNumber === d?.version) ??
    (versions.length > 0 ? versions[versions.length - 1] : null);

  return (
    <Dialog open={!!d} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        {d && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-8">{d.title ?? d.deliverableType}</DialogTitle>
              <DialogDescription>
                {d.deliverableType.replace(/_/g, " ")} · version {d.version}
                {d.taskTitle ? ` · from "${d.taskTitle}"` : ""}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5">
              {d.previewUrl && (
                <img
                  src={d.thumbnailUrl ?? d.previewUrl}
                  alt={d.title ?? d.deliverableType}
                  className="w-full max-h-80 object-contain rounded-md bg-muted"
                />
              )}

              {d.documentContent && (
                <div className="space-y-2">
                  <div className="rounded-md border bg-muted/30 p-3 max-h-56 overflow-y-auto">
                    <p className="text-xs whitespace-pre-wrap leading-relaxed">
                      {d.documentContent}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(d.documentContent ?? "");
                        toast({ title: "Copied" });
                      }}
                      data-testid="button-copy-document"
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => {
                        const blob = new Blob([d.documentContent ?? ""], {
                          type: "text/plain",
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${(d.title ?? d.deliverableType).replace(/[^\w-]+/g, "-")}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      data-testid="button-export-document"
                    >
                      <Download className="h-3 w-3 mr-1" /> Export
                    </Button>
                  </div>
                </div>
              )}

              {latest && (
                <ReviewPanel version={latest} />
              )}

              <Separator />

              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Version history
                </div>
                {versions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No attempts recorded yet.</p>
                ) : (
                  <div className="space-y-2" data-testid="deliverable-version-history">
                    {[...versions]
                      .sort((a, b) => b.versionNumber - a.versionNumber)
                      .map((v) => (
                        <VersionRow key={v.id} version={v} current={v.versionNumber === d.version} />
                      ))}
                  </div>
                )}
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Ask for a change
                </div>
                <Textarea
                  rows={3}
                  value={request}
                  onChange={(e) => setRequest(e.target.value)}
                  placeholder="Make the headline punchier and lead with the guarantee."
                  data-testid="input-revision-request"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                disabled={!request.trim() || revise.isPending}
                onClick={() => revise.mutate(request.trim())}
                data-testid="button-request-revision"
              >
                {revise.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                )}
                Request revision
              </Button>
              <Button
                disabled={approve.isPending || d.status === "approved"}
                onClick={() => approve.mutate()}
                data-testid="button-approve-deliverable"
              >
                {approve.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-1.5" />
                )}
                {d.status === "approved" ? "Approved" : "Approve"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReviewPanel({ version }: { version: CreativeStrategistDeliverableVersion }) {
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="deliverable-review">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="secondary"
          className={CS_REVIEW_VERDICT_CLASSES[version.reviewVerdict] ?? ""}
        >
          {CS_REVIEW_VERDICT_LABELS[version.reviewVerdict] ?? version.reviewVerdict}
        </Badge>
        {typeof version.reviewScore === "number" && (
          <span className="text-xs text-muted-foreground">Score {version.reviewScore}/100</span>
        )}
        {version.reviewedAt && (
          <span className="text-xs text-muted-foreground">
            {csFormatDateTime(version.reviewedAt)}
          </span>
        )}
      </div>
      {version.reviewSummary && <p className="text-xs">{version.reviewSummary}</p>}
      {version.issues.length > 0 && (
        <ul className="space-y-1">
          {version.issues.map((issue, i) => (
            <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
              <span className="mt-1 h-1 w-1 rounded-full bg-current shrink-0" />
              <span>
                {issue.area ? <span className="font-medium">{issue.area}: </span> : null}
                {issue.detail}
              </span>
            </li>
          ))}
        </ul>
      )}
      {version.criteriaChecks.length > 0 && (
        <div className="grid gap-1 pt-1">
          {version.criteriaChecks.map((c, i) => (
            <div key={i} className="text-[11px] flex items-start gap-1.5">
              {c.met ? (
                <Check className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
              ) : (
                <AlertTriangle className="h-3 w-3 mt-0.5 text-amber-600 shrink-0" />
              )}
              <span className={c.met ? "text-muted-foreground" : ""}>
                {c.criterion}
                {c.note ? ` — ${c.note}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VersionRow({
  version,
  current,
}: {
  version: CreativeStrategistDeliverableVersion;
  current: boolean;
}) {
  return (
    <div
      className="flex items-start gap-3 rounded-md border p-2.5"
      data-testid={`version-${version.versionNumber}`}
    >
      {version.thumbnailUrl ? (
        <img
          src={version.thumbnailUrl}
          alt={`Version ${version.versionNumber}`}
          loading="lazy"
          className="h-14 w-14 rounded object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="h-14 w-14 rounded bg-muted flex items-center justify-center shrink-0">
          <FileText className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium">Version {version.versionNumber}</span>
          {current && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5">
              Current
            </Badge>
          )}
          <Badge
            variant="secondary"
            className={`text-[10px] h-4 px-1.5 ${CS_REVIEW_VERDICT_CLASSES[version.reviewVerdict] ?? ""}`}
          >
            {CS_REVIEW_VERDICT_LABELS[version.reviewVerdict] ?? version.reviewVerdict}
          </Badge>
          {version.previewUrl && (
            <a
              href={version.previewUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
            >
              Open <ArrowUpRight className="h-3 w-3" />
            </a>
          )}
        </div>
        {version.reviewSummary && (
          <p className="text-[11px] text-muted-foreground">{version.reviewSummary}</p>
        )}
        {version.revisionInstructions.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            <span className="font-medium">Next attempt was told to:</span>{" "}
            {version.revisionInstructions.join(" ")}
          </p>
        )}
      </div>
    </div>
  );
}
